import supabase from "../lib/supabaseClient";
import type { SuggestionAnalysisResponse } from "../types/suggestions";

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (window.location.hostname === "localhost"
    ? "http://localhost:4000"
    : "https://api.sendupdates.ai");

/**
 * Get auth headers for API requests
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;

  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/**
 * Suggestions API Service
 * Handles communication with the backend suggestions endpoint
 */
export const suggestionsService = {
  /**
   * Analyze draft content and get improvement suggestions
   */
  async analyzeDraft(
    content: string,
    subject?: string,
    integrationIds?: string[]
  ): Promise<SuggestionAnalysisResponse> {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${API_BASE_URL}/api/suggestions/analyze`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          content,
          subject,
          integrationIds: integrationIds || [],
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to analyze draft:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Analysis failed",
      };
    }
  },

  /**
   * Dismiss a suggestion (for analytics/learning)
   */
  async dismissSuggestion(
    suggestionId: string,
    reason?: string
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${API_BASE_URL}/api/suggestions/dismiss`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          suggestionId,
          reason,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to dismiss suggestion:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Dismiss failed",
      };
    }
  },
};

export default suggestionsService;
